/* ==========================================================================
   Provider Tab Bar
   Navigation tabs for "New Prescription", "Saved", "History", "Catalog",
   "Pharmacy Notes" (when published) and "Help" views
   ========================================================================== */

import { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { usePortalSlug } from '@/context/PortalContext'
import { useAuth } from '@/hooks/useAuth'
import {
  FilePlus,
  Bookmark,
  History,
  Package,
  HelpCircle,
  NotebookText,
} from 'lucide-react'

const baseTabs = [
  { label: 'New Prescription', path: '/prescribe', icon: FilePlus },
  { label: 'Saved', path: '/saved', icon: Bookmark },
  { label: 'History', path: '/history', icon: History },
  { label: 'Catalog', path: '/catalog', icon: Package },
] as const

const notesTab = { label: 'Pharmacy Notes', path: '/notes', icon: NotebookText } as const
const helpTab = { label: 'Help', path: '/guide', icon: HelpCircle } as const

export function ProviderTabBar() {
  const slug = usePortalSlug()
  const location = useLocation()
  const { user } = useAuth()

  // PORTAL-159: the Pharmacy Notes tab only exists once the pharmacy has
  // published a note, so providers at pharmacies that don't use the feature
  // see the tab bar exactly as before.
  const tabs = user?.pharmacy_notes?.trim()
    ? [...baseTabs, notesTab, helpTab]
    : [...baseTabs, helpTab]

  // PORTAL-116: the bar scrolls sideways on phones, so make sure the current
  // tab is visible instead of leaving it clipped past the right edge.
  const activeRef = useRef<HTMLAnchorElement>(null)
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' })
  }, [location.pathname])

  return (
    <nav className="mb-6 border-b border-[var(--color-gray-200)]">
      {/* PORTAL-116: with five or more tabs the bar no longer fits a phone
          width, so it scrolls sideways instead of pushing the whole page. */}
      <div className="flex gap-0 overflow-x-auto">
        {tabs.map((tab) => {
          const fullPath = `/rx/${slug}${tab.path}`
          const isActive = location.pathname === fullPath

          return (
            <Link
              key={tab.path}
              ref={isActive ? activeRef : undefined}
              to={fullPath}
              className={`
                flex shrink-0 items-center gap-2 px-4 py-3 text-sm font-medium
                whitespace-nowrap
                border-b-2 transition-colors duration-150
                ${
                  isActive
                    ? 'border-[var(--pharmacy-primary)] text-[var(--pharmacy-primary)]'
                    : 'border-transparent text-[var(--color-gray-500)] hover:text-[var(--color-charcoal)] hover:border-[var(--color-gray-300)]'
                }
              `}
            >
              <tab.icon className="h-4 w-4" aria-hidden="true" />
              {tab.label}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

export default ProviderTabBar
